/**
 * The dropdown class.
 * Handles functionality for the Supernav's dropdowns.
 * 
 * TODO: There's probably a better way to architect some of this, but project timeline made working with existing functionality super attractive.
 */

/* Dependencies --------------------------------------------- */

import { traverseEventName as traversalTraverseEventName } from '../traversal'

import { exists } from '../../helpers/utilities'

/* Main class ----------------------------------------------- */

function mixin( props ) {

	const { node, config = {} } = props

    if ( ! node ) return false

    let state = {
        node,
		parent      : node.parentNode,
		link        : node.querySelector( config.linkSelector ),
        dropdown    : node.querySelector( config.dropdownSelector ),        
        isHovering  : false,
        isOpen      : false,
		openingEvent: null,
        justOpened  : false,
        justClosed  : false,
    }

	let boundEventHandlers = {}

    let module = {

		/** Main methods ---------------- */

		/**
		 * Sets default accessibility attributes on the menu item link.
		 */
		setDefaultAttributes() {
			state.link.setAttribute( 'role', 'button' )
			state.link.setAttribute( 'aria-haspopup', 'true' )
			state.link.setAttribute( 'aria-expanded', 'false' )
		},
		/**
		 * Opens the dropdown.
		 * Triggers all relevant events and sets all relevant properties.
		 */
        openDropdown( initiatingEventType ) {
			this.toggleState( true )
			state.parent[config.activeMenuItemAttribute] = state.node
			state.link.setAttribute( 'aria-expanded', 'true' )
			state.dropdown.classList.add( config.activeClass )
            this.toggleDropdownEvents()
			state.dropdown.dispatchEvent( new CustomEvent( openEventName, { bubbles: true } ) )
            state.dropdown.addEventListener( 'transitionend', boundEventHandlers.onOpenComplete )
        },

		/**
		 * Closes the dropdown.
		 * Triggers all relevant events and sets all relevant properties.
		 */
        closeDropdown() {
			this.toggleState( false )
            state.link.setAttribute( 'aria-expanded', 'false' )
            state.dropdown.classList.remove( config.activeClass )
			this.toggleDropdownEvents( false )
            state.dropdown.addEventListener( 'transitionend', boundEventHandlers.onClose )
        },

		/** Secondary methods ----------- */

		/**
		 * Toggles events pertaining to dropdowns.
		 */
        toggleDropdownEvents( toBind = true ) {
			const method = toBind ? 'addEventListener' : 'removeEventListener'
            state.node[method]( 'mouseenter', boundEventHandlers.onMenuItemHover )
            state.node[method]( 'mouseleave', boundEventHandlers.onMenuItemHover )
            state.node[method]( 'mouseleave', boundEventHandlers.onMenuItemMouseLeave )
            state.node[method]( escapeEventName, boundEventHandlers.onMenuItemEscape )
			state.dropdown[method]( traversalTraverseEventName, boundEventHandlers.onTraversal )
        },

		/** Handlers -------------------- */

		/** Dropdown elements ------ */		

		/**
		 * Handles menu item engagements — can be mouseenter or click.
		 * Opens the dropdown.
		 */
        onMenuItemEngage( event ) {

			if ( event.type === 'mouseenter' && window.innerWidth < config.hamburgerBreakpoint ) return

            if ( ! event.target.getAttribute( 'href' ) ) event.preventDefault()
            if ( state.justOpened ) return

            state.justOpened = true
			state.openingEvent = event.type
            this.openDropdown()

            // Double-click debouncing technique

            setTimeout( () => { state.justOpened = false }, config.interactionDebounce )
        },

		/**
		 * Handles hovering over a menu item.
		 * Tracks hover state internally
		 */
		onMenuItemHover( event ) {
			if ( window.innerWidth < config.hamburgerBreakpoint ) return
            state.isHovering = event.type === 'mouseenter'
        },

		/**
		 * Handles menu item mouseleave events.
		 * Closes the dropdown, but only after giving the user a buffer.
		 */
        onMenuItemMouseLeave() {

			if ( window.innerWidth < config.hamburgerBreakpoint ) return

            if ( state.justClosed ) return
            state.dropdown.dispatchEvent( new CustomEvent( beforeCloseEventName, { bubbles: true } ) )
            state.justClosed = true

			// A little buffer to prevent this from happening too often

            setTimeout( () => { state.justClosed = false }, config.interactionDebounce )
            state.isHovering = false

            // A little buffer so dropdown doesn't immediately close on mouseleave

            setTimeout(
				() => {
                	if ( state.isHovering ) return
                	this.closeDropdown()
            	},
				config.dropdownCloseBuffer
			)
        },

		/**
		 * Handles users pressing the ESC key while focusing inside of menu items.
		 * Closes the dropdown.
		 */
        onMenuItemEscape() {
            this.closeDropdown()
        },

		/**
		 * Handles clicking of sibling menu items.
		 * Closes the dropdown if the intent is to open a different dropdown.
		 */
        onMenuItemSiblingClick() {
            if ( state.parent[config.activeMenuItemAttribute] === state.node ) return
            this.closeDropdown()
        },

		/**
		 * Handles the completion of a dropdown opening.
		 * Triggers relevant events and cleans up relevant listeners.
		 */
        onOpenComplete( event ) {
			const isDropdown   = state.dropdown.isEqualNode( event.target )
			const isVisibility = event.propertyName === 'visibility'

            if ( ! isDropdown || ! isVisibility || ! state.isOpen ) return

            state.dropdown.dispatchEvent(
				new CustomEvent( openCompleteEventName, { bubbles: true, detail: { openingEvent: state.openingEvent } } )
			)
            state.dropdown.removeEventListener( 'transitionend', boundEventHandlers.onOpenComplete )
        },

		/**
		 * Handles the completion of a dropdown closing.
		 * Triggers relevant events and cleans up relevant listeners.
		 */
        onClose( event ) {
			const isDropdown   = state.dropdown.isEqualNode( event.target )
			const isVisibility = event.propertyName === 'visibility'

            if ( ! isDropdown || ! isVisibility || state.isOpen ) return

            state.dropdown.dispatchEvent( new CustomEvent( closeEventName, { bubbles: true } ) )
            state.dropdown.removeEventListener( 'transitionend', boundEventHandlers.onCloseComplete )
        },

		/** Traversal -------------- */

		/**
		 * Handles layer traversal.
		 * Closes the dropdown if the target layer is the dropdown or doesn't exist.
		 */
		onTraversal( event ) {
			this.closeDropdown()
		},

		/** Utility methods ------------- */

		/**
		 * Toggles the state of the menu item's dropdown.
		 * Used for internal tracking.
		 */
        toggleState( isOpen ) {
            state.isOpen = isOpen
        },

		/** Setup ----------------------- */

		/**
		 * Initializes the mixin and sets up initial event bindings.
		 */
        init() {

            if ( ! exists( state.dropdown ) ) return

			// We'll be binding and unbinding handlers when menu dropdowns are engaged
			// SO we have to store some references to bound handlers to prevent memory leaks
			this.setDefaultAttributes()
			
			boundEventHandlers.onMenuItemEngage     = this.onMenuItemEngage.bind( this )
			boundEventHandlers.onMenuItemHover      = this.onMenuItemHover.bind( this )
			boundEventHandlers.onMenuItemMouseLeave = this.onMenuItemMouseLeave.bind( this )
			boundEventHandlers.onMenuItemEscape     = this.onMenuItemEscape.bind( this )
			boundEventHandlers.onOpenComplete       = this.onOpenComplete.bind( this )
			boundEventHandlers.onClose              = this.onClose.bind( this )
			boundEventHandlers.onTraversal          = this.onTraversal.bind( this )

			// The only evergreen listeners are for links

			state.link.addEventListener( 'mouseenter', boundEventHandlers.onMenuItemEngage )
            state.link.addEventListener( 'click', boundEventHandlers.onMenuItemEngage )
        },
    }

    return module.init()
}

/* Export modules ------------------------------------------- */

export const beforeCloseEventName  = 'cuSupernavDropdownBeforeClose'
export const closeEventName        = 'cuSupernavDropdownClose'
export const escapeEventName       = 'cuSupernavDropdownEscape'
export const openEventName         = 'cuSupernavDropdownOpen'
export const openCompleteEventName = 'cuSupernavDropdownOpenComplete'

export default {

    init( config ) {

		if ( ! config.namespace ) return

		config = Object.assign(
			{

				// General config

				activeMenuItemAttribute: `${config.namespace}-activeMenuItem`,
				dropdownCloseBuffer    : 100,
				hamburgerBreakpoint    : 1024,
				interactionDebounce    : 50,
				activeClass            : `is-${config.namespace}-active`,

				// Selectors

				selector        : `.js-${config.namespace}__menu-item.has-${config.namespace}-children`,
				linkSelector    : `.js-${config.namespace}__menu-item-link`,
				dropdownSelector: `.js-${config.namespace}__dropdown`,

				// Modifier classes

				hasChildrenClass: `has-${config.namespace}-children`
			},
			config
		)

        let elements = document.querySelectorAll( config.selector )
        if ( ! elements.length ) return

        return [...elements].map( ( node ) => {
            return mixin( { node, config } )
        } )
    }
}
